import { 
  Calendar,
  Users,
  Shield,
  MapPin,
  CreditCard,
  BarChart3,
  Clock,
  Settings,
  Smartphone,
  AlertTriangle,
  CheckCircle,
  Bell,
  Zap,
  Globe,
  Lock,
  Database,
  Headphones,
  TrendingUp,
  Eye,
  Star,
  Wifi,
  Camera,
  Music,
  Thermometer,
  Coffee,
  Car,
  Ticket
} from 'lucide-react';

export const venueManagementSystemFeatures = {
  keyBenefits: [
    {
      title: 'Intelligent Venue Booking System',
      description: 'Advanced booking platform with real-time availability, automated conflict resolution, and dynamic pricing optimisation. Handles complex booking scenarios with room combinations and equipment allocation.',
      icon: Calendar,
      metrics: 'Real-time availability, Dynamic pricing, 95% booking efficiency, Automated conflicts',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      title: 'Comprehensive Event Coordination',
      description: 'End-to-end event management with timeline coordination, vendor integration, catering management, and automated workflow orchestration for seamless event execution.',
      icon: Users,
      metrics: 'Timeline coordination, Vendor integration, Workflow automation, Event analytics',
      color: 'from-purple-500 to-pink-600'
    },
    {
      title: 'Advanced Customer Experience Management',
      description: 'Personalised customer journeys with preference tracking, loyalty programmes, feedback systems, and automated communication workflows to enhance satisfaction.',
      icon: Star,
      metrics: 'Personalised journeys, Loyalty programmes, Feedback systems, 98% satisfaction',
      color: 'from-emerald-500 to-green-600'
    },
    {
      title: 'Integrated Financial Management',
      description: 'Complete financial oversight with automated invoicing, payment processing, revenue tracking, and comprehensive reporting for optimal financial performance.',
      icon: CreditCard,
      metrics: 'Automated invoicing, Payment processing, Revenue tracking, Financial reporting',
      color: 'from-green-500 to-emerald-600'
    },
    {
      title: 'Smart Facilities Oversight',
      description: 'IoT-enabled facilities management with environmental monitoring, equipment tracking, maintenance scheduling, and automated alerts for optimal venue conditions.',
      icon: MapPin,
      metrics: 'IoT monitoring, Equipment tracking, Maintenance scheduling, Environmental control',
      color: 'from-cyan-500 to-blue-600'
    },
    {
      title: 'Staff & Security Coordination',
      description: 'Comprehensive staff management with shift scheduling, access control integration, security protocols, and real-time communication systems.',
      icon: Shield,
      metrics: 'Shift scheduling, Access control, Security protocols, Real-time communication',
      color: 'from-red-500 to-pink-600'
    },
    {
      title: 'Multi-Venue Operations Hub',
      description: 'Centralised management for multiple venues with unified reporting, cross-venue resource sharing, and standardised operational procedures.',
      icon: Globe,
      metrics: 'Multi-venue control, Unified reporting, Resource sharing, Standardised procedures',
      color: 'from-violet-500 to-purple-600'
    },
    {
      title: 'Advanced Analytics & Insights',
      description: 'Comprehensive venue analytics with occupancy patterns, revenue optimisation, customer behaviour insights, and predictive performance modelling.',
      icon: BarChart3,
      metrics: 'Occupancy analytics, Revenue optimisation, Behaviour insights, Predictive modelling',
      color: 'from-orange-500 to-red-600'
    },
    {
      title: 'Mobile Event Management',
      description: 'Native mobile applications for staff and customers with real-time updates, mobile check-ins, digital programmes, and instant notifications.',
      icon: Smartphone,
      metrics: 'Native mobile apps, Real-time updates, Mobile check-ins, Instant notifications',
      color: 'from-teal-500 to-cyan-600'
    },
    {
      title: 'Equipment & Resource Management',
      description: 'Comprehensive equipment tracking with availability scheduling, maintenance logs, condition monitoring, and automated allocation systems.',
      icon: Settings,
      metrics: 'Equipment tracking, Availability scheduling, Maintenance logs, Automated allocation',
      color: 'from-amber-500 to-yellow-600'
    },
    {
      title: 'Digital Ticketing & Access Control',
      description: 'Integrated ticketing system with QR codes, digital passes, access control integration, and real-time capacity monitoring.',
      icon: Ticket,
      metrics: 'Digital ticketing, QR codes, Access control, Capacity monitoring',
      color: 'from-indigo-500 to-blue-600'
    },
    {
      title: 'Catering & Hospitality Integration',
      description: 'Complete catering management with menu planning, dietary requirements, service coordination, and quality assurance systems.',
      icon: Coffee,
      metrics: 'Menu planning, Dietary requirements, Service coordination, Quality assurance',
      color: 'from-pink-500 to-rose-600'
    }
  ],
  
  advancedCapabilities: [
    {
      title: 'AI-Powered Venue Optimisation',
      description: 'Machine learning algorithms analyse usage patterns, predict demand, optimise space allocation, and recommend pricing strategies for maximum revenue.',
      icon: Zap,
      metrics: 'ML algorithms, Demand prediction, Space optimisation, Revenue maximisation',
      color: 'from-yellow-500 to-orange-600'
    },
    {
      title: 'Dynamic Pricing & Revenue Management',
      description: 'Sophisticated pricing engine with demand-based adjustments, competitor analysis, seasonal variations, and yield management optimisation.',
      icon: TrendingUp,
      metrics: 'Dynamic pricing, Demand-based adjustments, Competitor analysis, Yield management',
      color: 'from-green-500 to-emerald-600'
    },
    {
      title: 'Smart Environmental Control',
      description: 'Automated climate control with IoT sensors, energy optimisation, lighting management, and air quality monitoring for optimal comfort.',
      icon: Thermometer,
      metrics: 'Automated climate control, Energy optimisation, Lighting management, Air quality',
      color: 'from-blue-500 to-cyan-600'
    },
    {
      title: 'Advanced Security & Surveillance',
      description: 'Comprehensive security system with CCTV integration, facial recognition, emergency protocols, and incident management workflows.',
      icon: Camera,
      metrics: 'CCTV integration, Facial recognition, Emergency protocols, Incident management',
      color: 'from-red-500 to-pink-600'
    },
    {
      title: 'Virtual & Hybrid Event Capabilities',
      description: 'Integrated virtual event platform with live streaming, audience interaction, hybrid participation, and digital engagement tools.',
      icon: Globe,
      metrics: 'Live streaming, Audience interaction, Hybrid participation, Digital engagement',
      color: 'from-purple-500 to-indigo-600'
    },
    {
      title: 'Predictive Maintenance System',
      description: 'AI-driven maintenance scheduling with condition monitoring, failure prediction, automated work orders, and vendor coordination.',
      icon: Settings,
      metrics: 'Condition monitoring, Failure prediction, Automated work orders, Vendor coordination',
      color: 'from-orange-500 to-red-600'
    },
    {
      title: 'Customer Journey Analytics',
      description: 'Advanced customer behaviour tracking with heat mapping, journey analysis, satisfaction scoring, and personalisation engines.',
      icon: Eye,
      metrics: 'Heat mapping, Journey analysis, Satisfaction scoring, Personalisation engines',
      color: 'from-teal-500 to-cyan-600'
    },
    {
      title: 'Multi-Language & Accessibility Support',
      description: 'Comprehensive accessibility features with multi-language support, visual/hearing assistance, mobility accommodations, and inclusive design.',
      icon: Users,
      metrics: 'Multi-language support, Visual/hearing assistance, Mobility accommodations, Inclusive design',
      color: 'from-indigo-500 to-blue-600'
    },
    {
      title: 'Integrated Marketing & Promotion',
      description: 'Built-in marketing tools with social media integration, email campaigns, promotional management, and customer acquisition tracking.',
      icon: Star,
      metrics: 'Social media integration, Email campaigns, Promotional management, Acquisition tracking',
      color: 'from-pink-500 to-rose-600'
    },
    {
      title: 'Advanced Reporting & Business Intelligence',
      description: 'Comprehensive business intelligence with custom dashboards, automated reports, KPI tracking, and strategic performance insights.',
      icon: BarChart3,
      metrics: 'Custom dashboards, Automated reports, KPI tracking, Strategic insights',
      color: 'from-violet-500 to-purple-600'
    },
    {
      title: 'Transport & Parking Integration',
      description: 'Smart transport coordination with parking management, public transport integration, ride-sharing partnerships, and accessibility services.',
      icon: Car,
      metrics: 'Parking management, Transport integration, Ride-sharing partnerships, Accessibility services',
      color: 'from-emerald-500 to-green-600'
    },
    {
      title: 'Audio-Visual & Technical Support',
      description: 'Comprehensive AV management with equipment scheduling, technical support coordination, live event monitoring, and quality assurance.',
      icon: Music,
      metrics: 'Equipment scheduling, Technical support, Live monitoring, Quality assurance',
      color: 'from-amber-500 to-yellow-600'
    }
  ],
  
  securityPerformance: [
    {
      title: 'Enterprise-Grade Security Framework',
      description: 'Military-grade security with end-to-end encryption, multi-factor authentication, role-based access control, and comprehensive audit trails.',
      icon: Shield,
      metrics: 'End-to-end encryption, Multi-factor authentication, Role-based access, Audit trails',
      color: 'from-red-500 to-pink-600'
    },
    {
      title: 'High-Performance Cloud Infrastructure',
      description: '99.9% uptime SLA with global CDN, auto-scaling capabilities, load balancing, and sub-second response times for optimal performance.',
      icon: Zap,
      metrics: '99.9% uptime SLA, Global CDN, Auto-scaling, Sub-second response times',
      color: 'from-yellow-500 to-orange-600'
    },
    {
      title: 'Data Protection & Compliance',
      description: 'Comprehensive data protection with GDPR compliance, data localisation, privacy controls, and secure data transfer protocols.',
      icon: Lock,
      metrics: 'GDPR compliance, Data localisation, Privacy controls, Secure transfer protocols',
      color: 'from-gray-500 to-slate-600'
    },
    {
      title: 'Real-Time System Monitoring',
      description: 'Continuous system monitoring with proactive alerting, performance optimisation, health checks, and 24/7 technical support.',
      icon: Eye,
      metrics: 'Continuous monitoring, Proactive alerting, Performance optimisation, 24/7 support',
      color: 'from-cyan-500 to-blue-600'
    },
    {
      title: 'Scalable Architecture Design',
      description: 'Cloud-native architecture with unlimited scalability, microservices design, API-first approach, and global distribution capabilities.',
      icon: TrendingUp,
      metrics: 'Unlimited scalability, Microservices design, API-first approach, Global distribution',
      color: 'from-green-500 to-emerald-600'
    },
    {
      title: 'Business Continuity & Disaster Recovery',
      description: 'Comprehensive backup solutions with automated recovery, geographic redundancy, failover systems, and 99.99% data durability.',
      icon: Database,
      metrics: 'Automated recovery, Geographic redundancy, Failover systems, 99.99% data durability',
      color: 'from-blue-500 to-indigo-600'
    }
  ]
};